// src/pages/LoginPage.jsx
import { useState } from "react";
import BookyaraLogo from "../components/ui/BookyaraLogo";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogIn, Eye, EyeOff, Scissors } from "lucide-react";
import { doc, getDoc, collection, query, where, limit, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../context/AuthContext";
import GoogleButton from "../components/auth/GoogleButton";

export default function LoginPage() {
  const { login, signInWithGoogle } = useAuth();
  const navigate       = useNavigate();
  const location       = useLocation();

  const [form,    setForm]    = useState({ email: "", password: "" });
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw,  setShowPw]  = useState(false);

  // ProtectedRoute passes destination via state.from; query param is a fallback
  const params   = new URLSearchParams(location.search);
  const redirect = location.state?.from?.pathname
    ? location.state.from.pathname + (location.state.from.search || "")
    : params.get("redirect");

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  }

  async function routeAfterLogin(uid) {
    if (redirect) { navigate(redirect); return; }
    const profileSnap = await getDoc(doc(db, "users", uid));
    const profile = profileSnap.data();
    if (profile?.role === "admin" && profile?.tenantId) {
      const tenantSnap = await getDoc(doc(db, "tenants", profile.tenantId));
      const slug = tenantSnap.data()?.slug;
      if (slug) { navigate(`/${slug}/admin`); return; }
    }
    if (profile?.role === "staff") { navigate("/staff-dashboard"); return; }
    // Try tenantId on profile first
    const tenantId = profile?.tenantId;
    if (tenantId) {
      const tenantSnap = await getDoc(doc(db, "tenants", tenantId));
      const slug = tenantSnap.data()?.slug;
      if (slug) { navigate(`/${slug}`); return; }
    }
    // Fallback: find tenant from user's bookings
    const bookingSnap = await getDocs(
      query(collection(db, "bookings"), where("userId", "==", uid), limit(1))
    );
    if (!bookingSnap.empty) {
      const tid = bookingSnap.docs[0].data().tenantId;
      if (tid) {
        const tenantSnap = await getDoc(doc(db, "tenants", tid));
        const slug = tenantSnap.data()?.slug;
        if (slug) { navigate(`/${slug}`); return; }
      }
    }
    navigate("/");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.email || !form.password) { setError("Please fill in all fields."); return; }
    setLoading(true);
    try {
      const cred = await login(form.email, form.password);
      await routeAfterLogin(cred.user.uid);
    } catch (err) {
      const msgs = {
        "auth/user-not-found":    "No account found with this email.",
        "auth/wrong-password":    "Incorrect password. Please try again.",
        "auth/invalid-credential":"Incorrect email or password.",
        "auth/invalid-email":     "Please enter a valid email address.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
      };
      setError(msgs[err.code] || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    const cred = await signInWithGoogle();
    await routeAfterLogin(cred.user.uid);
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-100 rounded-full blur-3xl opacity-60" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-50 rounded-full blur-3xl opacity-80" />
      </div>

      <div className="relative w-full max-w-md animate-scale-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} className="inline-flex flex-col items-center group">
            <BookyaraLogo size="md" className="mb-4" />
          </button>
          <p className="text-ink-400 text-sm mt-1">Online booking for small businesses</p>
        </div>

        <div className="auth-card">
          <h2 className="font-semibold text-xl text-ink-900 mb-1">Welcome back</h2>
          <p className="text-ink-400 text-sm mb-6">Sign in to manage your bookings</p>

          <GoogleButton onClick={handleGoogle} label="Continue with Google" />

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 border-t border-surface-200" />
            <span className="text-ink-400 text-xs">or sign in with email</span>
            <div className="flex-1 border-t border-surface-200" />
          </div>

          {error && <div className="alert-error mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email Address</label>
              <input name="email" type="email" placeholder="you@email.com"
                value={form.email} onChange={handleChange}
                className="form-input" autoComplete="email" />
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <input name="password" type={showPw ? "text" : "password"}
                  placeholder="Your password"
                  value={form.password} onChange={handleChange}
                  className="form-input pr-11" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-brand-500 transition-colors">
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-brand-600 hover:text-brand-700 font-medium">
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2">
              {loading
                ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <><LogIn size={16} /> Sign In</>
              }
            </button>
          </form>

          <p className="text-center text-sm text-ink-400 mt-6">
            Don't have an account?{" "}
            <Link to={`/signup${location.search}`}
              className="text-brand-600 hover:text-brand-700 font-semibold">
              Create one free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
